import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';
import { 
  MdSearch, 
  MdClose, 
  MdRefresh,
  MdBusinessCenter,
  MdInfo,
  MdChevronRight
} from 'react-icons/md';
import { FaFilePdf, FaFileExcel, FaFilter, FaCalendarAlt, FaUser, FaFileContract, FaBook, FaClock } from 'react-icons/fa';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const SLAReportPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false);
  
  // Report data
  const [reportData, setReportData] = useState([]);
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // useEffect kaldırıldı - rapor sadece butona basıldığında yüklenecek

  // Rapor oluştur
  const generateReport = async () => {
    try {
      setIsLoading(true);
      
      // API'den SLA rapor verilerini getir
      const response = await api.get('/sla-report', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setReportData(response.data || []);
      setReportGenerated(true);
      setCurrentPage(1);
      
    } catch (error) {
      console.error('Error generating SLA report:', error);
      alert(t('reports.slaReportGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  // Filtreleri temizle
  // Amaç: Oluşturulmuş raporu sıfırlamak ve sayfalamayı başa almak
  const handleClearFilters = () => {
    setReportGenerated(false);
    setReportData([]);
    setCurrentPage(1);
  };

  // Excel export
  const exportToExcel = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    // Helper function for clean text
    const cleanTextForExcel = (text) => {
      if (!text) return '';
      return text.toString().replace(/undefined/g, '');
    };

    // Prepare data for Excel
    const excelData = reportData.map((item, index) => ({
      [t('reports.serialNumber')]: index + 1,
      [t('reports.slaName')]: cleanTextForExcel(item.sla_name),
      [t('reports.slaGroup')]: cleanTextForExcel(item.sla_group),
      [t('reports.companyName')]: cleanTextForExcel(item.customer_name),
      [t('reports.totalTickets')]: cleanTextForExcel(item.total_tickets),
      [t('reports.open')]: cleanTextForExcel(item.open_tickets),
      [t('reports.inProgress')]: cleanTextForExcel(item.in_progress_tickets),
      [t('reports.closed')]: cleanTextForExcel(item.closed_tickets)
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 8 },   // Sira No
      { wch: 25 },  // SLA Adı
      { wch: 20 },  // SLA Grubu
      { wch: 30 },  // Müşteri
      { wch: 15 },  // Toplam Ticket
      { wch: 10 },  // Açık
      { wch: 12 },  // İşlemde
      { wch: 12 }   // Kapalı
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('reports.slaReport'));

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.slaReport').replace(/\s+/g, '_')}_${currentDate}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  // PDF export  
  const exportToPDF = () => {
    if (reportData.length === 0) {
      alert(t('reports.noDataToExport'));
      return;
    }

    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation

    // Helper function to handle Turkish characters
    const cleanText = (text) => {
      if (!text) return '';
      
      // Convert Turkish characters to Latin equivalents for PDF compatibility
      const turkishToLatin = {
        'ç': 'c', 'Ç': 'C',
        'ğ': 'g', 'Ğ': 'G', 
        'ı': 'i', 'I': 'I',
        'İ': 'I', 'i': 'i',
        'ö': 'o', 'Ö': 'O',
        'ş': 's', 'Ş': 'S',
        'ü': 'u', 'Ü': 'U'
      };
      
      let cleanedText = text.toString().replace(/undefined/g, '');
      
      // Replace Turkish characters
      for (const [turkish, latin] of Object.entries(turkishToLatin)) {
        cleanedText = cleanedText.replace(new RegExp(turkish, 'g'), latin);
      }
      
      return cleanedText;
    };

    // Add title
    doc.setFontSize(16);
    doc.text(t('reports.slaReport'), 14, 22);

    // Add generation date
    doc.setFontSize(10);
    doc.text(`${t('reports.reportDate')}: ${new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR')}`, 14, 30);

    // Prepare table data with clean text
    const tableData = reportData.map((item, index) => [
      index + 1,
      cleanText((item.sla_name || '').substring(0, 20) + (item.sla_name?.length > 20 ? '...' : '')),
      cleanText((item.sla_group || '').substring(0, 15) + (item.sla_group?.length > 15 ? '...' : '')),
      cleanText((item.customer_name || '').substring(0, 20) + (item.customer_name?.length > 20 ? '...' : '')),
      cleanText(item.total_tickets),
      cleanText(item.open_tickets),
      cleanText(item.in_progress_tickets),
      cleanText(item.closed_tickets)
    ]);

    // Add table
    autoTable(doc, {
      head: [['#', t('reports.slaName'), t('reports.slaGroup'), t('reports.companyName'), t('reports.totalTickets'), t('reports.open'), t('reports.inProgress'), t('reports.closed')]],
      body: tableData,
      startY: 40,
      styles: {
        fontSize: 7,
        cellPadding: 1,
        font: 'helvetica',
        fontStyle: 'normal'
      },
      headStyles: {
        fillColor: [66, 139, 202],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        font: 'helvetica'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 12 },  // #
        1: { cellWidth: 35 },  // SLA Adı
        2: { cellWidth: 25 },  // SLA Grubu
        3: { cellWidth: 35 },  // Müşteri
        4: { cellWidth: 15 },  // Toplam
        5: { cellWidth: 12 },  // Açık
        6: { cellWidth: 15 },  // İşlemde
        7: { cellWidth: 15 }   // Kapalı
      },
      margin: { left: 10, right: 10 },
      tableWidth: '100%'
    });

    // Generate filename with current date
    const currentDate = new Date().toLocaleDateString(localStorage.getItem('language') === 'en' ? 'en-US' : 'tr-TR').replace(/\./g, '-');
    const filename = `${t('reports.slaReport').replace(/\s+/g, '_')}_${currentDate}.pdf`;

    // Save file
    doc.save(filename);
  };

  // Pagination logic
  const pageSizeOptions = [10, 20, 50, 100];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = reportData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(reportData.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(Number(newSize));
    setCurrentPage(1);
  };

  return (
    <div className="h-full overflow-auto">
      {/* Breadcrumb */}
      <div className="flex justify-between items-center mb-6">
        <nav className="flex items-center text-sm">
          <div className="flex items-center bg-[#232b3b] px-4 py-2 rounded-md shadow-sm">
            <span className="text-[#fff] font-medium">
              {t('common.reports')}
            </span>
            <MdChevronRight className="mx-2 text-[#fff]" size={20} />
            <span className="text-[#fff] font-medium">
              {t('reports.slaReport')}
            </span>
          </div>
        </nav>
      </div>

      {/* Action Buttons */}
      <div className=" dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.slaInfo')}</h1>
        </div>
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={generateReport}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-[#232b3b] text-white rounded-md hover:bg-[#1e2230] disabled:opacity-50"
            >
              <MdSearch size={16} />
              {isLoading ? t('reports.generating') : t('common.show')}
            </button>

            <button
              onClick={handleClearFilters}
              disabled={isLoading}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50 dark:bg-gray-500 dark:text-white dark:hover:bg-gray-600"
            >
              <MdClose size={16} />
              {t('reports.clearFilters')}
            </button>

            <button
              onClick={exportToExcel}
              disabled={!reportGenerated || reportData.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaFileExcel size={16} />
              {t('reports.downloadExcel')}
            </button>

            <button
              onClick={exportToPDF}
              disabled={!reportGenerated || reportData.length === 0}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:cursor-not-allowed"
            >
              <FaFilePdf size={16} />
              {t('reports.downloadPDF')}
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
          <h1 className="text-lg font-medium text-gray-800 dark:text-white">{t('reports.report')}</h1>
        </div>
        
        {reportGenerated ? (
          <>
            {/* Sayfa boyutu seçici */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('common.show')}</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handlePageSizeChange(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {pageSizeOptions.map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-gray-600 dark:text-gray-300">{t('table.records')}</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {t('table.totalRecordsFound', { count: reportData.length })}
                </span>
              </div>
            </div>

            {reportData.length > 0 ? (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full table-auto">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 shadow-sm">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.slaName')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.slaGroup')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.companyName')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.totalTickets')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.open')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.inProgress')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">{t('reports.closed')}</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {currentData.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {item.sla_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.sla_group}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                            {item.customer_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              {item.total_tickets}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              {item.open_tickets}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.in_progress_tickets}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              {item.closed_tickets}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3 p-4">
                  {currentData.map((item, index) => (
                    <div key={index} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                      <div className="grid grid-cols-1 gap-3">
                        {/* SLA Name */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                            {t('reports.slaName')}:
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white text-right">
                            {item.sla_name}
                          </span>
                        </div>

                        {/* SLA Group and Company Row */}
                        <div className="grid grid-cols-1 gap-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                              {t('reports.slaGroup')}:
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                              {item.sla_group}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                              {t('reports.companyName')}:
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300 text-right">
                              {item.customer_name}
                            </span>
                          </div>
                        </div>

                        {/* Tickets Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.totalTickets')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              {item.total_tickets}
                            </span>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.open')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                              {item.open_tickets}
                            </span>
                          </div>
                        </div>

                        {/* In Progress and Closed Row */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.inProgress')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {item.in_progress_tickets}
                            </span>
                          </div>
                          <div className="text-center">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                              {t('reports.closed')}
                            </div>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                              {item.closed_tickets}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sayfalama */}
                {totalPages > 1 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 sm:px-6">
                    <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between mb-6">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          {t('table.showing')}{' '}
                          <span className="font-medium">{indexOfFirstItem + 1}</span>
                          {' '}-{' '}
                          <span className="font-medium">
                            {Math.min(indexOfLastItem, reportData.length)}
                          </span>{' '}
                          / {t('table.total')}{' '}
                          <span className="font-medium">{reportData.length}</span>{' '}
                          {t('table.records')}
                        </p>
                      </div>
                      <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-medium ${
                              currentPage === 1
                                ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {t('table.previous')}
                          </button>
                          {[...Array(totalPages)].map((_, index) => (
                            <button
                              key={index + 1}
                              onClick={() => handlePageChange(index + 1)}
                              className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                                currentPage === index + 1
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                              }`}
                            >
                              {index + 1}
                            </button>
                          ))}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`relative inline-flex items-center rounded-r-md px-3 py-2 text-sm font-medium ${
                              currentPage === totalPages
                                ? 'bg-gray-100 dark:bg-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {t('table.next')}
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                <p>{t('reports.noDataFound')}</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-gray-500 dark:text-gray-400">
            <p>{t('reports.useFiltersToGenerate')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SLAReportPage;
